export const TRAINER_PORTAL_SESSION_KEY = 'kilo-trainer-portal'

/** Remember trainer portal mode for the browser tab (local dev). */
export function markTrainerPortalSession(): void {
  try {
    sessionStorage.setItem(TRAINER_PORTAL_SESSION_KEY, '1')
  } catch {
    /* private browsing */
  }
}

export function clearTrainerPortalSession(): void {
  try {
    sessionStorage.removeItem(TRAINER_PORTAL_SESSION_KEY)
  } catch {
    /* private browsing */
  }
}

/** True when the app should render the trainer portal instead of the athlete app. */
export function isTrainerPortal(): boolean {
  if (typeof window === 'undefined') return false

  const { hostname, search, pathname } = window.location

  if (hostname === 'trainer.kilo.app' || hostname.endsWith('.trainer.kilo.app')) {
    return true
  }

  // Staging / preview hosts (e.g. trainer-*.pages.dev)
  if (hostname.startsWith('trainer-') || hostname.startsWith('trainer.')) {
    return true
  }

  const params = new URLSearchParams(search)
  if (params.has('trainer')) {
    return true
  }

  if (pathname === '/trainer' || pathname.startsWith('/trainer/')) {
    return true
  }

  // Local dev: stay on trainer portal after first visit with ?trainer=1
  if (import.meta.env.DEV) {
    try {
      if (sessionStorage.getItem(TRAINER_PORTAL_SESSION_KEY) === '1') {
        return true
      }
    } catch {
      /* ignore */
    }
  }

  return false
}

/** Athlete app entry — clears trainer tab mode in local dev. */
export function athletePortalEntryUrl(): string {
  clearTrainerPortalSession()
  return import.meta.env.PROD ? 'https://app.kilo.app' : `${window.location.origin}/`
}

/** Canonical trainer portal entry URL for the current environment. */
export function trainerPortalEntryUrl(): string {
  if (import.meta.env.PROD) {
    return 'https://trainer.kilo.app'
  }
  return `${window.location.origin}/?trainer=1`
}

/** Supabase magic-link / OAuth redirect target for coach sign-in. */
export function trainerPortalRedirectUrl(): string {
  return trainerPortalEntryUrl()
}

function isSupabaseAuthCallback(search: string, hash: string): boolean {
  if (hash.includes('access_token') || hash.includes('error=')) return true
  const params = new URLSearchParams(search)
  return params.has('code') || params.get('type') === 'magiclink' || params.get('type') === 'recovery'
}

function redirectToTrainerPortal(search: string, hash: string): void {
  try {
    const url = new URL(trainerPortalEntryUrl())
    const incoming = new URLSearchParams(search)
    incoming.forEach((value, key) => {
      url.searchParams.set(key, value)
    })
    window.location.replace(`${url.toString()}${hash}`)
  } catch {
    window.location.replace(`${trainerPortalEntryUrl()}${search}${hash}`)
  }
}

/**
 * When Supabase auth completes on the athlete origin (Site URL fallback) but the
 * intended destination was the trainer portal, reroute before booting the athlete app.
 * Returns true if a full-page redirect was started.
 */
export function rerouteToTrainerPortalIfNeeded(): boolean {
  if (typeof window === 'undefined') return false

  const { pathname, search, hash, hostname } = window.location

  // Production coach links on the athlete domain → trainer subdomain.
  if (
    import.meta.env.PROD &&
    hostname !== 'trainer.kilo.app' &&
    !hostname.endsWith('.trainer.kilo.app') &&
    !hostname.startsWith('trainer-') &&
    !hostname.startsWith('trainer.') &&
    (pathname === '/trainer' || pathname.startsWith('/trainer/'))
  ) {
    redirectToTrainerPortal(search, hash)
    return true
  }

  if (isTrainerPortal()) return false

  if (!isSupabaseAuthCallback(search, hash)) {
    return false
  }

  const params = new URLSearchParams(search)
  const redirectTo = decodeURIComponent(params.get('redirect_to') ?? '')
  if (redirectTo.includes('trainer.kilo.app') || redirectTo.includes('trainer=1')) {
    redirectToTrainerPortal(search, hash)
    return true
  }

  return false
}

/** Normalize /trainer paths so trainer BrowserRouter routes match. */
export function normalizeTrainerPortalPath(): void {
  if (typeof window === 'undefined') return
  const { pathname, search, hash } = window.location
  if (!pathname.startsWith('/trainer')) return

  const stripped = pathname.replace(/^\/trainer\/?/, '/')
  const nextPath = stripped === '/' ? '/' : stripped
  window.history.replaceState(null, '', `${nextPath}${search}${hash}`)
}
